import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { RefreshCcw, TrendingUp } from 'lucide-react'
import { api } from '@/api/client'
import type { SpikeView } from '@/api/types'
import { formatTime } from '@/lib/utils'
import { Badge, Button, Card, EmptyState, StatCard } from '@/admin-design/_common'
import CreateScriptModal from '@/components/CreateScriptModal'

export default function Dashboard() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const stats = useQuery({ queryKey: ['stats'], queryFn: api.stats, refetchInterval: 10000 })
  const version = useQuery({ queryKey: ['version'], queryFn: api.version })
  const spikesQ = useQuery({ queryKey: ['recent-spikes'], queryFn: api.recentSpikes })

  const [topic, setTopic] = useState<SpikeView | null>(null)

  const refreshM = useMutation({
    mutationFn: api.refreshTopics,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recent-spikes'] }),
  })

  const spikes = spikesQ.data ?? []

  return (
    <div className="space-y-4">
      <Card>
        <header className="flex items-center gap-2 border-b border-border px-3 py-2">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-card-foreground">
            <TrendingUp size={14} /> 트렌딩 토픽
          </span>
          <span className="text-[11px] text-muted-foreground">{spikes.length}개 · 클릭해 스크립트 생성</span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="ml-auto h-7 px-2 text-xs"
            onClick={() => refreshM.mutate()}
            loading={refreshM.isPending}
          >
            {!refreshM.isPending && <RefreshCcw size={12} />} 새로고침
          </Button>
        </header>
        <div className="p-3">
          {spikesQ.isLoading ? (
            <p className="text-xs text-muted-foreground">불러오는 중…</p>
          ) : spikes.length === 0 ? (
            <EmptyState
              title="아직 감지된 트렌딩 토픽이 없어요."
              description="새로고침으로 트렌드 수집을 강제하거나, 트렌드 수집 설정을 확인하세요."
            />
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {spikes.map((sp) => (
                <button
                  key={sp.topic_id}
                  type="button"
                  onClick={() => setTopic(sp)}
                  className="flex flex-col gap-1 rounded-md border border-border bg-card p-3 text-left shadow-sm transition hover:border-ring hover:bg-muted/30"
                >
                  <span className="text-sm font-medium text-card-foreground line-clamp-2">{sp.title}</span>
                  <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Badge className="rounded text-[10px]">score {sp.score.toFixed(2)}</Badge>
                    {formatTime(Math.floor(new Date(sp.seen_at).getTime() / 1000))}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Users" value={stats.data?.users ?? 0} />
        <StatCard label="Active sessions" value={stats.data?.active_sessions ?? 0} />
      </section>

      <Card>
        <Card.Body>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Signed in as</h2>
          <p className="text-lg text-card-foreground">{me.data?.email ?? '…'}</p>
          <p className="text-sm text-muted-foreground">{me.data?.name}</p>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Build</h2>
          <p className="text-xs text-muted-foreground">
            commit: <code>{version.data?.commit ?? 'dev'}</code> · built_at:{' '}
            <code>{version.data?.built_at ?? 'unknown'}</code>
          </p>
        </Card.Body>
      </Card>

      {topic && (
        <CreateScriptModal
          topic={topic}
          onClose={() => setTopic(null)}
          onCreated={(id) => {
            setTopic(null)
            qc.invalidateQueries({ queryKey: ['scripts'] })
            navigate(`/scripts/${id}`)
          }}
        />
      )}
    </div>
  )
}
